/**
 * useTitleSearchRowDetails
 *
 * Backend gaps behind the Title Search home (design.md, Backend gap 5):
 * - `listJobsByUser` maps only the DD/BA fields, so a TR row arrives without
 *   its property, borrower, order number or step and analysis statuses.
 * - The latest `title_grade` is not on the job at all.
 *
 * Until the list exposes them, this hook fills the gap from the same sources
 * the Workspace uses: `getJob` for the TR fields and `getTitleAnalysisResults`
 * for the grade of a completed run. Requests run with limited concurrency, are
 * cached per job and re-run only when the row's status signature changes.
 * Failures leave the row on its list data and a "—" grade.
 *
 * Requirements: 2.5, 11.11
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { JobType, type Job } from '../../graphql/types';
import { getJob } from '../../services/workflowService';
import { getTitleAnalysisResults } from '../../services/guidelinesApiService';
import { isGrade, type Grade } from './featureHomeModel';

/** At most this many rows are enriched at once. */
export const ENRICH_CONCURRENCY = 3;

export interface TitleSearchRowDetails {
  /** Full job as returned by `getJob`, or null if it could not be loaded. */
  job: Job | null;
  /** Grade of the latest completed run, or null when unknown. */
  grade: Grade | null;
}

/**
 * Run `fn` over `items` with at most `limit` in flight. Results keep the input
 * order. A rejection from `fn` is the caller's to handle (wrap `fn`).
 */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T) => Promise<R>
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;
  const worker = async (): Promise<void> => {
    while (next < items.length) {
      const index = next++;
      results[index] = await fn(items[index]);
    }
  };
  const workers = Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, worker);
  await Promise.all(workers);
  return results;
}

/** Changes whenever a list-level field that can move a TR row changes. */
export function rowSignature(job: Job): string {
  return [job.status, job.documentProcessingStatus, job.startedAt, job.completedAt].join('|');
}

/**
 * The list job with its empty (null or undefined) fields filled from the full
 * job. Fields the list already carries win, so a fresher subscription update
 * is never overwritten by an older fetch.
 */
export function mergeJobDetails(listJob: Job, full: Job | null | undefined): Job {
  if (!full) return listJob;
  const merged: Record<string, unknown> = { ...(full as unknown as Record<string, unknown>) };
  for (const [key, value] of Object.entries(listJob as unknown as Record<string, unknown>)) {
    if (value !== null && value !== undefined) merged[key] = value;
  }
  return merged as unknown as Job;
}

interface CacheEntry {
  signature: string;
  details: TitleSearchRowDetails;
}

async function loadDetails(job: Job, tenantId: string): Promise<TitleSearchRowDetails> {
  let full: Job | null = null;
  try {
    full = await getJob(job.jobId, tenantId);
  } catch (err) {
    console.error('Failed to load title search details:', err);
  }
  const analysisStatus = (full?.titleAnalysisStatus ?? job.titleAnalysisStatus ?? '')
    .trim()
    .toLowerCase();
  let grade: Grade | null = null;
  if (analysisStatus === 'completed') {
    try {
      const results = await getTitleAnalysisResults(job.jobId);
      const value = results.report?.title_grade;
      grade = isGrade(value) ? value : null;
    } catch (err) {
      console.error('Failed to load title grade:', err);
    }
  }
  return { job: full, grade };
}

/**
 * Details for TR rows, keyed by job id. Returns an empty map for other
 * Features (`enabled` false) without issuing any request.
 */
export function useTitleSearchRowDetails(
  jobs: readonly Job[],
  enabled: boolean
): ReadonlyMap<string, TitleSearchRowDetails> {
  const [details, setDetails] = useState<ReadonlyMap<string, TitleSearchRowDetails>>(
    () => new Map()
  );
  const cacheRef = useRef<Map<string, CacheEntry>>(new Map());
  const inFlightRef = useRef<Set<string>>(new Set());
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  // Re-run only when a row is added or its signature changes.
  const pendingKey = useMemo(
    () =>
      enabled
        ? jobs
            .filter((j) => j.jobType === JobType.TR)
            .map((j) => `${j.jobId}:${rowSignature(j)}`)
            .join(',')
        : '',
    [jobs, enabled]
  );

  useEffect(() => {
    if (!enabled) return;
    const pending = jobs.filter((j) => {
      if (j.jobType !== JobType.TR) return false;
      if (inFlightRef.current.has(j.jobId)) return false;
      return cacheRef.current.get(j.jobId)?.signature !== rowSignature(j);
    });
    if (pending.length === 0) return;

    pending.forEach((j) => inFlightRef.current.add(j.jobId));
    void (async () => {
      let tenantId = 'default-tenant';
      try {
        const session = await fetchAuthSession();
        tenantId =
          (session.tokens?.idToken?.payload['custom:tenantId'] as string) || 'default-tenant';
      } catch (err) {
        console.error('Failed to read session for title search details:', err);
      }
      await mapWithConcurrency(pending, ENRICH_CONCURRENCY, async (job) => {
        const loaded = await loadDetails(job, tenantId);
        inFlightRef.current.delete(job.jobId);
        if (!aliveRef.current) return;
        cacheRef.current.set(job.jobId, { signature: rowSignature(job), details: loaded });
        setDetails((prev) => new Map(prev).set(job.jobId, loaded));
      });
    })();
    // `jobs` is read inside, but only a change to `pendingKey` should re-run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingKey, enabled]);

  return details;
}
